import { apiInternals, send } from './client';

/**
 * The escalation chain (M-10, Tasks 5-6) — `GET/POST /escalations`, the four
 * transition routes (`advance`/`resolve`/`close`/`comment`) and
 * `GET`/`PUT /escalation-routes`. Every shape here is verified field-for-field
 * against `App\Http\Resources\Escalations\{EscalationResource,
 * EscalationEventResource,EscalationRouteResource}` and
 * `App\Support\OpenApi\ApiSpecification`'s own escalation schemas in the API
 * repository, not inferred from the task brief's prose.
 *
 * ## `list` is built on `request`, never `send` (`requests.ts`'s own rule)
 *
 * `GET /escalations` is cursor-paginated exactly like `GET /case-requests` —
 * `{ data, meta: { next_cursor, per_page, total } }`. `send()` resolves to
 * `request()`'s `.data` alone and throws `meta` away.
 *
 * ## `mine` serialises as `1`/`0`, never `true`/`false` (spec §2)
 *
 * Laravel's `boolean` validation rule accepts `true`/`false`/`1`/`0`/`'1'`/
 * `'0'` — the STRING `'true'` (`String(true)`) is rejected. `queryString()`
 * below carries the identical fix `requests.ts` documents at length.
 *
 * ## Actions come from the server, never re-derived here
 *
 * `EscalationResource`'s own `status`/`level` are the only truth this module
 * carries about what may happen next — nothing here re-implements
 * `EscalationTransition`'s own rules (terminal-state refusal, level-3
 * exhaustion, the level-3 close-note requirement). A caller renders what the
 * record currently says and lets the four action calls below answer with
 * whatever the server decides, including a `409`/`422` this module does not
 * anticipate.
 *
 * ## `escalationRoutesApi` reads/writes a BARE list, never paginated
 *
 * `GET`/`PUT /escalation-routes` both answer `{ data: EscalationRoute[] }`
 * with no `meta` at all (`EscalationRouteCollectionEnvelope`'s own schema) —
 * `send()` is therefore the correct choice here, unlike `list` above.
 */

/** The four states `EscalationResource.status` can carry (spec E-3). */
export type EscalationStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

/**
 * What `GET /escalations` filters on — three queues plus `all`
 * (`EscalationIndexRequest::rules()`'s own `Rule::in`). `open` covers BOTH
 * the model's `open` AND `in_progress` (`EscalationController::index()`'s
 * own comment) — there is no separate "in progress" queue to select.
 */
export type EscalationStatusFilter = 'open' | 'resolved' | 'closed' | 'all';

/** The seven values `EscalationEventResource.action` can carry — the full
 * enum `ApiSpecification`'s own `EscalationEvent` schema declares, including
 * `reassigned`/`reopened`, which no write path in this milestone produces
 * yet but which the wire type still has to admit. */
export type EscalationEventAction =
  | 'opened'
  | 'escalated'
  | 'reassigned'
  | 'commented'
  | 'resolved'
  | 'reopened'
  | 'closed';

/** One row of an escalation's own history — `EscalationEventResource`'s own shape, oldest first. */
export interface EscalationEventRow {
  uuid: string;
  from_level: 1 | 2 | 3 | null;
  to_level: 1 | 2 | 3 | null;
  action: EscalationEventAction;
  actor_id: number | null;
  actor_name: string | null;
  notes: string | null;
  occurred_at: string | null;
}

/** `EscalationResource`'s `case` key — `uuid` + `case_number`, never the integer foreign key. `null` for a caseless escalation (spec E-1). */
export interface EscalationCaseRef {
  uuid: string;
  case_number: string;
}

/**
 * One escalation, exactly as `GET /escalations`, `GET /escalations/
 * {escalation}`, `POST /escalations` and all four transition routes render
 * it — the SAME shape everywhere (`EscalationResource`'s own class
 * docblock).
 */
export interface EscalationRow {
  uuid: string;
  case: EscalationCaseRef | null;
  mobile: string;
  case_number: string | null;
  level: 1 | 2 | 3;
  status: EscalationStatus;
  reason: string | null;
  description: string | null;
  assigned_to: number | null;
  assignee_name: string | null;
  created_by: number | null;
  opened_at: string | null;
  resolved_at: string | null;
  resolved_by: number | null;
  resolution_notes: string | null;
  /** THE HISTORY, AND THE POINT OF THE FEATURE (spec §3) — always present, oldest first. */
  events: readonly EscalationEventRow[];
}

/** Every filter `GET /escalations` accepts (`EscalationIndexRequest::rules()`). All optional. */
export interface EscalationQuery {
  status?: EscalationStatusFilter;
  mine?: boolean;
  case_number?: string;
  mobile?: string;
  cursor?: string;
  per_page?: number;
}

/** One page of the chain — cursor-paginated, exactly `CaseRequestPage`'s shape. */
export interface EscalationPage {
  data: readonly EscalationRow[];
  meta: {
    next_cursor: string | null;
    per_page: number;
    /** How many escalations match the selected filters, not how many are on this page. */
    total: number;
  };
}

/** `POST /escalations`'s body (`StoreEscalationRequest`) — only `mobile` is required (spec E-1). */
export interface StoreEscalationInput {
  mobile: string;
  case_number?: string | null;
  reason?: string | null;
  description?: string | null;
}

/** The one body shape shared by `advance`/`resolve`/`close`/`comment` (`EscalationActionRequest`) — `notes` is optional for all four on the wire; `comment`'s own controller refuses a blank one with a `422`. */
export interface EscalationActionInput {
  notes?: string | null;
}

/** A route's own level, `escalation_routes.level` (1-3). */
export type EscalationRouteLevel = 1 | 2 | 3;

/**
 * One `escalation_routes` row — `EscalationRouteResource`'s own shape.
 * `dispensary_id`/`role_id`/`user_id` are each the related row's UUID, never
 * the integer foreign key (that resource's own docblock).
 */
export interface EscalationRouteRow {
  uuid: string;
  dispensary_id: string | null;
  level: EscalationRouteLevel;
  role_id: string | null;
  role_name: string | null;
  user_id: string | null;
  user_name: string | null;
  category: string | null;
  is_active: boolean;
}

/** One row of `PUT /escalation-routes`'s `routes` array (`UpdateEscalationRoutesRequest`). Exactly one of `role_id`/`user_id`, never both, never neither. */
export interface EscalationRouteInput {
  level: EscalationRouteLevel;
  role_id?: string | null;
  user_id?: string | null;
  category?: string | null;
}

/** `PUT /escalation-routes`'s whole body — replaces the active set for ONE dispensary (`dispensary_id: null` for the global fallback), never edits a row in place (spec E-5). */
export interface UpdateEscalationRoutesInput {
  dispensary_id: string | null;
  routes: readonly EscalationRouteInput[];
}

const { request } = apiInternals;

/**
 * `?key=value&...`, or `''` when every value is absent. `undefined` is
 * dropped rather than becoming the literal string `'undefined'` in the URL —
 * the identical helper `requests.ts`/`cases.ts`/`client.ts` each redeclare
 * locally for the same reason (none is exported).
 */
function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | boolean | undefined][]) {
    if (value === undefined) continue;

    // A BOOLEAN GOES OVER THE WIRE AS `1`/`0`, NEVER `true`/`false` — see this
    // module's own docblock for why `mine=true` would 422.
    search.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

export const escalationsApi = {
  /**
   * DELIBERATELY NOT `send<EscalationPage>()` — see this module's own
   * docblock for why that would compile and still lose `meta.next_cursor`.
   */
  list: async (query: EscalationQuery = {}): Promise<EscalationPage> => {
    const { data, meta } = await request<readonly EscalationRow[]>(`/escalations${queryString(query)}`);
    return { data, meta: meta as EscalationPage['meta'] };
  },

  get: (uuid: string) => send<EscalationRow>(`/escalations/${uuid}`),

  create: (input: StoreEscalationInput) =>
    send<EscalationRow>('/escalations', { method: 'POST', body: input }),

  /** `409` when the escalation is already `resolved`/`closed`, or already at level 3 — the message names both alternatives (spec E-3). Never predicted client-side; see this module's own docblock. */
  advance: (uuid: string, input: EscalationActionInput = {}) =>
    send<EscalationRow>(`/escalations/${uuid}/advance`, { method: 'POST', body: input }),

  /** `409` when the escalation is already `resolved`/`closed`. */
  resolve: (uuid: string, input: EscalationActionInput = {}) =>
    send<EscalationRow>(`/escalations/${uuid}/resolve`, { method: 'POST', body: input }),

  /** `409` when already terminal; `422` naming `notes` when the escalation is at level 3 with a blank note (spec E-3). */
  close: (uuid: string, input: EscalationActionInput = {}) =>
    send<EscalationRow>(`/escalations/${uuid}/close`, { method: 'POST', body: input }),

  /** `422` naming `notes` for a blank comment; `409` when already terminal. */
  comment: (uuid: string, input: EscalationActionInput = {}) =>
    send<EscalationRow>(`/escalations/${uuid}/comment`, { method: 'POST', body: input }),
};

export const escalationRoutesApi = {
  /** Every route row, active and inactive alike (`EscalationRouteController::index()`'s own docblock). No `meta` — a bare list, unlike `escalationsApi.list`. */
  list: () => send<readonly EscalationRouteRow[]>('/escalation-routes'),

  /** Replaces the whole active set for ONE dispensary — see this module's own docblock. Answers the target dispensary's active rows after the replace. */
  update: (input: UpdateEscalationRoutesInput) =>
    send<readonly EscalationRouteRow[]>('/escalation-routes', { method: 'PUT', body: input }),
};
